/**
 * userAuthStore — learner auth slice (Zustand).
 *
 *   POST /api/auth/login               POST /api/auth/logout
 *   POST /api/auth/refresh             POST /api/auth/register
 *   POST /api/auth/google              POST /api/auth/change-password
 *   POST /api/auth/forgot-password     POST /api/auth/reset-password
 *   POST /api/auth/resend-verification POST /api/auth/verify-email
 */
import { apiRequest } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import authSession from "../../lib/authSession";
import {saveLearnerAvatar} from "../../lib/format";

export const userAuthInitialState = {
  userToken: null,
  user: null,
  userIsAuthenticated: false,

  userLoginLoading: false,
  userLoginError: null,

  userAuthActionLoading: false,
  userAuthActionError: null,
};

/**
 * Mirror the learner *display* keys the learner UI reads (`image`/`uemail` in
 * UserNavBar & UserNavBar2, `isAdmin` elsewhere). No tokens are stored — auth
 * lives in httpOnly cookies.
 */
function persistUserSession({ email, user }) {
  try {
    localStorage.setItem("isAdmin", "false");
    localStorage.setItem("email", email || user?.email || "");
    localStorage.setItem("uemail", email || user?.email || "");
    localStorage.setItem(
      "name",
      user
        ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || "User"
        : "User"
    );
    saveLearnerAvatar(user);
  } catch {
    /* storage unavailable — the session still lives in authSession/memory */
  }
}

/** Remove the flat learner keys on logout. */
function clearUserSession() {
  try {
    for (const key of ["token", "refreshToken", "isAdmin", "email", "uemail", "name", "image"]) {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

/** Read display-only claims from Google's ID credential after the API accepts it. */
function googleClaimsFromCredential(credential) {
  try {
    const encoded = String(credential || "").split(".")[1];
    if (!encoded) return {};
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")));
  } catch {
    return {};
  }
}

export function createUserAuthActions(set, get) {
  /** Run a POST auth action. No credentials: the API's CORS policy rejects
   *  credentialed requests, so auth is bearer-token based. */
  async function runAction(path, body, successMessage) {
    set({ userAuthActionLoading: true, userAuthActionError: null });
    try {
      // Auth endpoint → cookies only.
      const data = await apiRequest(get, path, { method: "POST", body, credentials: "include" });
      set({
        userAuthActionLoading: false,
        userAuthActionError: null,
        successMessage,
      });
      return { success: true, data };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ userAuthActionLoading: false, userAuthActionError: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // POST /api/auth/login
    userLogin: async (email, password) => {
      set({ userLoginLoading: true, userLoginError: null });
      try {
        // Auth endpoint → cookies only; the body token is kept in memory.
        const data = await apiRequest(get, "/api/auth/login", {
          method: "POST",
          body: { email, password },
          token: "",
          credentials: "include",
        });
        const user = data.user || data.data?.user || null;
        // Auth endpoint = cookie-based. The access token from the body is kept
        // IN MEMORY for every other endpoint's Bearer header.
        const token = authSession.findAccessToken(data) || null;
        const refreshToken = data?.refreshToken || data?.data?.refreshToken || null;
        // Auth endpoint = cookie-based; the body token is the session (memory +
        // this tab only) and the role comes from its `role` claim.
        authSession.setSession({ token, role: "user", user, refreshToken });
        persistUserSession({ email, user });
        set({
          userLoginLoading: false,
          userLoginError: null,
          userToken: token,
          userIsAuthenticated: true,
          user,
        });
        return { success: true, user, data };
      } catch (err) {
        const msg = toDisplayString(err.message, "Login failed");
        set({ userLoginLoading: false, userLoginError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/auth/google — exchange the Google ID token (GIS credential)
    // for the same cookie session a password login gets.
    userGoogleLogin: async (credential) => {
      set({ userLoginLoading: true, userLoginError: null });
      try {
        const data = await apiRequest(get, "/api/auth/google", {
          method: "POST",
          body: { credential },
          token: "",
          credentials: "include",
        });
        const apiUser = data.user || data.data?.user || null;
        const googleClaims = googleClaimsFromCredential(credential);
        const user = apiUser || googleClaims.picture || googleClaims.email || googleClaims.name
          ? {
              ...googleClaims,
              ...(apiUser || {}),
              picture: apiUser?.picture || apiUser?.avatar || googleClaims.picture,
              email: apiUser?.email || googleClaims.email,
              name: apiUser?.name || googleClaims.name,
            }
          : null;
        const token = authSession.findAccessToken(data) || null;
        const refreshToken = data?.refreshToken || data?.data?.refreshToken || null;
        authSession.setSession({ token, role: "user", user, refreshToken });
        persistUserSession({ email: user?.email, user });
        set({
          userLoginLoading: false,
          userLoginError: null,
          userToken: token,
          userIsAuthenticated: true,
          user,
        });
        return { success: true, user, data };
      } catch (err) {
        const msg = toDisplayString(err.message, "Google sign-in failed");
        set({ userLoginLoading: false, userLoginError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/auth/logout
    userLogout: async () => {
      set({ userAuthActionLoading: true, userAuthActionError: null });
      try {
        // Auth endpoint → cookies only.
        await apiRequest(get, "/api/auth/logout", { method: "POST", credentials: "include" });
      } catch {
        // Best-effort
      }
      await authSession.logout("user");
      clearUserSession();
      set({
        userAuthActionLoading: false,
        userToken: null,
        userIsAuthenticated: false,
        user: null,
        userProfile: null,
      });
      return { success: true };
    },

    // POST /api/auth/refresh — the cookies re-authenticate the session.
    userRefresh: async () => {
      set({ userAuthActionLoading: true, userAuthActionError: null });
      try {
        const session = await authSession.refresh("user");
        set({
          userAuthActionLoading: false,
          userIsAuthenticated: true,
          user: session?.user ?? get().user,
          userToken: session?.token ?? get().userToken,
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to refresh session");
        set({ userAuthActionLoading: false, userAuthActionError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/auth/register
    userRegister: (payload) =>
      runAction("/api/auth/register", payload, "Registration successful"),

    // POST /api/auth/change-password  ({ currentPassword, newPassword })
    userChangePassword: (payload) =>
      runAction("/api/auth/change-password", payload, "Password changed successfully"),

    // POST /api/auth/forgot-password  ({ email })
    userForgotPassword: (payload) =>
      runAction("/api/auth/forgot-password", payload, "Reset link sent"),

    // POST /api/auth/reset-password  ({ token, password })
    userResetPassword: (payload) =>
      runAction("/api/auth/reset-password", payload, "Password reset successfully"),

    // POST /api/auth/resend-verification  ({ email })
    userResendVerification: (payload) =>
      runAction("/api/auth/resend-verification", payload, "Verification email sent"),

    // POST /api/auth/verify-email  ({ token })
    userVerifyEmail: (payload) => runAction("/api/auth/verify-email", payload, "Email verified"),
  };
}

const userAuthActions = createUserAuthActions;
export default userAuthActions;
